import React, { useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import { PrimaryButton, QuaternaryButton, SecondaryButton } from 'components/uiLibrary/LinkButton';

import EntityName from 'components/Globals/EntityName';

import { RESPONSE_STATUS_CODES } from 'constants/index';

import { getShuffledArray } from 'utils/globals';
import { sentryCaptureMessage } from 'utils/globals/sentry';
import useAppContext from 'utils/hooks/useAppContext';
import useNavigate from 'utils/hooks/useNavigate';

import { ENTITY_TYPES } from 'operabase-router/constants';

import ErrorContent from '../ErrorContent';
import PageWrapper from '../PageWrapper';

import classes from './PageError.module.scss';

const CODE_CONTENT = {
  [RESPONSE_STATUS_CODES.PERMANENT_REDIRECT]: {
    message: 'Moved Permanently!',
    content: 'The URL requested has been moved permanently.',
  },
  [RESPONSE_STATUS_CODES.TEMPORARY_REDIRECT]: {
    message: 'Temporary Redirect!',
    content: 'The URL requested has been moved temporarily.',
  },
  [RESPONSE_STATUS_CODES.BAD_REQUEST]: {
    message: 'Bad Request!',
    content: 'You have used invalid syntax.',
  },
  [RESPONSE_STATUS_CODES.UNAUTHORIZED]: {
    message: 'Unauthorized!',
    content: 'The URL requested requires authorization.',
  },
  [RESPONSE_STATUS_CODES.FORBIDDEN]: {
    message: 'Forbidden!',
    content: 'You are not permitted to access the requested URL.',
  },
  [RESPONSE_STATUS_CODES.NOT_FOUND]: {
    message: 'Not Found!',
    content: 'The requested URL was not found on this server.',
  },
  [RESPONSE_STATUS_CODES.METHOD_NOT_ALLOWED]: {
    message: 'Method Not Allowed!',
    content: 'The method used is not permitted.',
  },
  [RESPONSE_STATUS_CODES.INTERNAL_SERVER_ERROR]: {
    message: 'Internal Server Error!',
    content: 'The requested URL caused an error on the server.',
  },
  [RESPONSE_STATUS_CODES.BAD_GATEWAY]: {
    message: 'Bad Gateway!',
    content: 'This server received an invalid response from an upstream server it accessed to fulfill the request.',
  },
  [RESPONSE_STATUS_CODES.SERVICE_UNAVAILABLE]: {
    message: 'Service Unavailable!',
    content: 'The Service is not available at the moment due to a temporary overloading or maintenance of the server.',
  },
  UNKNOWN: {
    message: 'Page Error!',
    content: 'The requested URL caused an error on the page.',
  },
};

const ContextualActions = () => {
  const navigate = useNavigate();
  const { obRouteContext } = useAppContext();
  const { entityType, entity } = obRouteContext?.page || {};
  const entityMapValues = useMemo(() => {
    const items = Object.values(obRouteContext?.entityMap || {}).flat();

    if (items?.length > 10) {
      return getShuffledArray(items, 10);
    }

    return items;
  }, [obRouteContext?.entityMap]);

  const { pageLinkProps, otherEntityLinkProps } = useMemo(() => {
    let primaryLinkProps = null;

    if (entityType && entity?.id) {
      primaryLinkProps = navigate.getLinkProps({
        entityType,
        entity,
      });
    }

    const otherPageEntities = entityMapValues.reduce((acc, item) => {
      if (
        [
          ENTITY_TYPES.ORGANIZATION,
          ENTITY_TYPES.PROFILE,
          ENTITY_TYPES.COUNTRY,
          ENTITY_TYPES.WORK_TYPE,
          ENTITY_TYPES.WORK,
        ].includes(item.entityType) &&
        item?.entityType !== entityType &&
        item?.entity?.id !== entity?.id
      ) {
        acc.push({
          entity: item?.entity,
          entityType: item?.entityType,
          linkProps: navigate.getLinkProps({
            entityType: item?.entityType,
            entity: item?.entity,
          }),
        });
      }

      return acc;
    }, []);

    return {
      pageLinkProps: primaryLinkProps,
      otherEntityLinkProps: otherPageEntities,
    };
  }, [entityType, entity, navigate, entityMapValues]);

  return (
    <>
      {pageLinkProps && (
        <PrimaryButton size="small" shape="rectangle" {...pageLinkProps} title={entity?.name} isLink>
          <EntityName entity={entity} entityType={entityType} />
        </PrimaryButton>
      )}
      {otherEntityLinkProps?.map(item => (
        <SecondaryButton size="small" shape="rectangle" {...item?.linkProps} title={item?.entity?.name} isLink>
          <EntityName entity={item?.entity} entityType={item?.entityType} />
        </SecondaryButton>
      ))}
    </>
  );
};

const ErrorContentWrapper = ({ statusCode, errorId }) => {
  const content = CODE_CONTENT[statusCode] || CODE_CONTENT.UNKNOWN;

  const footer = useMemo(
    () => (
      <div className={classes.actions}>
        <div className={classes.actions__btns}>
          {statusCode !== RESPONSE_STATUS_CODES.INTERNAL_SERVER_ERROR && <ContextualActions />}
        </div>
        <div className={classes.actions__btns}>
          <QuaternaryButton size="small" shape="rectangle" href="/[[..action]]" as="/" isLink>
            Operabase Home
          </QuaternaryButton>
        </div>
      </div>
    ),
    [statusCode],
  );

  return <ErrorContent title={content?.message} description={content?.content} footer={footer} errorId={errorId} />;
};

const PageError = ({ statusCode, error }) => {
  const [errorId, setErrorId] = useState(null);
  const { obRouteContext } = useAppContext();
  const router = useRouter();
  const content = CODE_CONTENT[statusCode] || CODE_CONTENT.UNKNOWN;

  const canRedirect = useMemo(
    () =>
      [RESPONSE_STATUS_CODES.PERMANENT_REDIRECT, RESPONSE_STATUS_CODES.TEMPORARY_REDIRECT].includes(statusCode) &&
      obRouteContext?.redirect,
    [obRouteContext, statusCode],
  );

  useEffect(() => {
    if (!errorId) {
      const {
        breadcrumbs: _breadcrumbs,
        entityMap: _entityMap,
        h1Title: _h1Title,
        linkProps: _linkProps,
        mainPath: _mainPath,
        tokenized: _tokenized,
        ...restObRouteContext
      } = obRouteContext || {};

      const sentryErrorId = sentryCaptureMessage(content?.message, {
        extra: {
          error,
          statusCode,
          canRedirect,
          ...restObRouteContext,
        },
      });

      if (sentryErrorId) {
        setErrorId(sentryErrorId);
      }
    }
  }, [content, obRouteContext, statusCode, error, errorId, canRedirect]);

  useEffect(() => {
    if (canRedirect) {
      router.replace(obRouteContext.redirect);
    }
  }, [canRedirect, router, obRouteContext?.redirect]);

  if (canRedirect) {
    return null;
  }

  if (statusCode === RESPONSE_STATUS_CODES.INTERNAL_SERVER_ERROR) {
    return <ErrorContentWrapper statusCode={statusCode} errorId={errorId} />;
  }

  return (
    <PageWrapper skipModals>
      <ErrorContentWrapper statusCode={statusCode} errorId={errorId} />
    </PageWrapper>
  );
};

export default PageError;
